import Leaflet from "leaflet";
import { AnyAction } from "redux";

import { AppState, Thunk } from "../App";

import { BACK_TO_START_SCREEN } from "./appState";
import { Websocket, socketSend } from "./websockets";

export interface WaterDepthImage {
  // objectUrl is an image created from a Blob using window.URL.createObjectURL
  // It is unique for each water depth image so we also use it as a sort of ID
  objectUrl: string;
  // The bounds used to generate this image; display it there.
  bounds: Leaflet.LatLngBounds;
  // Each image sent has a number saying for which time it was generated;
  // when the image is done loading, we sent it back as "processed" over
  // the websocket so the backend knows how far we are.
  timing: number;
}

interface WaterdepthState {
  waterDepthImages: WaterDepthImage[]; // MAX LENGTH 3 - one shown, one being loaded, one buffer
  oldWaterDepthImages: WaterDepthImage[]; // So the memory can be freed.
}

const INITIAL_STATE: WaterdepthState = {
  waterDepthImages: [],
  oldWaterDepthImages: [],
};

// Action types
const RECEIVE_WATER_DEPTH_IMAGE = "waterdepth/RECEIVE_WATER_DEPTH_IMAGE";

interface ReceiveWaterDepthImageAction {
  type: typeof RECEIVE_WATER_DEPTH_IMAGE;
  image: WaterDepthImage;
}
const WATER_DEPTH_IMAGE_LOADED = "waterdepth/WATER_DEPTH_IMAGE_LOADED";

interface WaterDepthImageLoadedAction {
  type: typeof WATER_DEPTH_IMAGE_LOADED;
  image: WaterDepthImage;
}

const REMOVE_WATER_DEPTH_IMAGES = "waterdepth/REMOVE_WATER_DEPTH_IMAGES";

interface RemoveWaterDepthImagesAction {
  type: typeof REMOVE_WATER_DEPTH_IMAGES;
  objectUrls: string;
}

// Reducer

export default function reducer(state = INITIAL_STATE, action: AnyAction): WaterdepthState {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case RECEIVE_WATER_DEPTH_IMAGE: {
      const waterDepthImage = (action as ReceiveWaterDepthImageAction).image;
      if (state.waterDepthImages.length >= 3) {
        return {
          waterDepthImages: [state.waterDepthImages[0], state.waterDepthImages[1], waterDepthImage],
          oldWaterDepthImages: [...state.oldWaterDepthImages, ...state.waterDepthImages.slice(2)],
        };
      } else {
        return {
          ...state,
          waterDepthImages: [...state.waterDepthImages, waterDepthImage],
        };
      }
    }
    case WATER_DEPTH_IMAGE_LOADED: {
      // Note that in this case also a message has to be sent to the backend, but that
      // is in the thunk, not in a reducer that cannot have side effects.
      // Which image was it?
      const image = (action as WaterDepthImageLoadedAction).image;
      const index = state.waterDepthImages.findIndex((wdi) => wdi.objectUrl === image.objectUrl);

      if (index === -1) return state; // Huh.
      if (index === 0) return state; // OK, don't need to do anything.

      // Move all images *before* this one to old images.
      return {
        waterDepthImages: state.waterDepthImages.slice(index),
        oldWaterDepthImages: [
          ...state.oldWaterDepthImages,
          ...state.waterDepthImages.slice(0, index),
        ],
      };
    }
    case REMOVE_WATER_DEPTH_IMAGES:
      // Keep the water depth images that are not in action.toBeRemoved
      return {
        ...state,
        oldWaterDepthImages: state.oldWaterDepthImages.filter(
          (wdi) => (action as RemoveWaterDepthImagesAction).objectUrls.indexOf(wdi.objectUrl) === -1
        ),
      };
    default:
      return state;
  }
}

// Action creators
export const receiveWaterDepthImage = (
  objectUrl: string,
  bounds: Leaflet.LatLngBounds,
  timing: number
) => {
  return {
    type: RECEIVE_WATER_DEPTH_IMAGE,
    image: { objectUrl, bounds, timing },
  };
};

export const waterDepthImageLoaded =
  (image: WaterDepthImage): Thunk =>
  (dispatch, getState) => {
    dispatch({
      type: WATER_DEPTH_IMAGE_LOADED,
      image,
    });
    dispatch(socketSend({ processed: image.timing }, Websocket.Waterdepths));

    const oldWaterDepthImages = getState().waterdepth.oldWaterDepthImages;
    if (oldWaterDepthImages) {
      oldWaterDepthImages.forEach((image) => window.URL.revokeObjectURL(image.objectUrl));
      dispatch({
        type: REMOVE_WATER_DEPTH_IMAGES,
        objectUrls: oldWaterDepthImages.map((image) => image.objectUrl),
      });
    }
  };

// Selectors
export const getWaterDepthImages = (state: AppState) => state.waterdepth.waterDepthImages;
